/* Post-pay CTA hierarchy.
 *
 * Plan §"Post-pay CTA":
 *   - PRIMARY: "Save my account" → triggers OTP → verify-otp → link-account.
 *   - SECONDARY: "Continue without account" — silently skips, no friction.
 *   - Once link-account succeeds, primary swaps to a confirmation chip.
 *   - Idempotency-Key is automatic (ApiClient).
 *
 * This component takes a `paymentId` (from the just-completed transaction)
 * and either prompts sign-in or — when already authenticated — links
 * directly.
 *
 * U1/S5: signed-in / auto-link chrome only when session after `/me`
 * (isSignedInNavChrome).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { SignInScreen } from '../auth/SignInScreen.js';
import { SignedInAccountPromptCard } from '../auth/components/SignedInAccountPromptCard.js';
import { canMountEmbeddedSignInForm } from '../auth/routing/canMountEmbeddedSignInForm.js';
import { useLinkAccountToTransaction } from '../auth/authApi.js';
import { resetShopCart } from '../shop/cart/resetShopCart.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  isTransactionLinkedToOtherCustomerError,
  linkAccountIdempotencyKey} from './linkAccountHelpers.js';
import { parkPostPayAuthReturnTarget } from './parkPostPayAuthReturn.js';
import {
  beginPostPayAuthClaimAndClearPending,
  clearPendingPostPayAuthAction,
  endPostPayAuthClaimInFlight,
  isPostPayAuthClaimGenerationCurrent,
  isPostPayAuthClaimInFlightFor,
  recordPostPayAuthActionOutcome,
  savePendingPostPayLinkClaim,
} from './postPayPendingAuthAction.js';
import { useHasSucceededPostPayAuthOutcome } from './usePostPayAuthActionOutcome.js';
import { isSignedInNavChrome } from '../hub/customerHubPaths.js';
import { toCustomerFacingErrorMessage } from '../../shared/errors/toCustomerFacingErrorMessage.js';

const LINK_ACCOUNT_UI_TIMEOUT_MS = 15_000;

interface PostPayCtaProps {
  paymentId: string;
  /** Shop hand-off: clear persisted cart once when post-pay UI mounts (parent may have cleared on COMPLETED). */
  cartClearKioskId?: number | null;
  onContinue?: () => void;
}

export function PostPayCta({
  paymentId,
  cartClearKioskId = null,
  onContinue}: PostPayCtaProps): JSX.Element {
  const { t } = useTranslation('customer');
  const location = useLocation();
  const { session } = useCustomerSession();
  // U1/S5 / G5: signed-in / auto-link only when session after /me.
  const signedInChrome = isSignedInNavChrome({ session });
  const tenantCode = useTenantCode();
  const [showSignIn, setShowSignIn] = useState(false);
  const [linked, setLinked] = useState(false);
  const [autoLinkSuppressed, setAutoLinkSuppressed] = useState(false);
  const [linkTimedOut, setLinkTimedOut] = useState(false);
  const linkAccount = useLinkAccountToTransaction();
  const clearedOnMountRef = useRef(false);
  const autoLinkAttemptedRef = useRef(false);
  /** Generation this CTA began — timeout ends claim only while we still own it. */
  const claimGenerationRef = useRef<number | null>(null);
  // G1 — live outcome sync when runner settles while CTA is already mounted.
  const outcomeLinked = useHasSucceededPostPayAuthOutcome({
    kind: 'link-account',
    paymentId,
  });
  const showLinked = linked || outcomeLinked;

  useEffect(() => {
    if (cartClearKioskId !== null && !clearedOnMountRef.current) {
      clearedOnMountRef.current = true;
      resetShopCart({
        tenantCode,
        kioskId: cartClearKioskId,
        source: 'post-pay-cta'});
    }
  }, [cartClearKioskId, tenantCode]);

  const handleLink = useCallback((customerIdFromAuth?: number): void => {
    if (autoLinkAttemptedRef.current) {
      return;
    }
    // Prefer customerId from auth land (onSignedIn) — React session may still be null.
    const customerId = customerIdFromAuth ?? session?.customerId;
    if (customerId === undefined) {
      return;
    }
    if (
      customerIdFromAuth === undefined &&
      !isSignedInNavChrome({ session })
    ) {
      return;
    }
    if (outcomeLinked || linked) {
      setLinked(true);
      return;
    }
    // Runner/CTA already owns this link — wait for reactive outcome (no second POST).
    if (isPostPayAuthClaimInFlightFor({ kind: 'link-account', paymentId })) {
      return;
    }
    // Claim first; matching pending cleared only if begin succeeds (generation token).
    const generation = beginPostPayAuthClaimAndClearPending({
      kind: 'link-account',
      paymentId,
    });
    if (generation === null) {
      // Begin failed — leave pending untouched; do not mutate.
      return;
    }
    setAutoLinkSuppressed(false);
    setLinkTimedOut(false);
    autoLinkAttemptedRef.current = true;
    claimGenerationRef.current = generation;
    linkAccount.mutate(
      {
        paymentId,
        customerId,
        idempotencyKey: linkAccountIdempotencyKey(paymentId, customerId)},
      {
        onSuccess: () => {
          // Stale after TTL / newer owner — do not clear pending or record.
          if (
            !isPostPayAuthClaimGenerationCurrent({
              kind: 'link-account',
              paymentId,
              generation,
            })
          ) {
            return;
          }
          clearPendingPostPayAuthAction();
          recordPostPayAuthActionOutcome({ kind: 'link-account', paymentId });
          endPostPayAuthClaimInFlight({ kind: 'link-account', paymentId });
          claimGenerationRef.current = null;
          setLinked(true);
        },
        onError: () => {
          if (
            isPostPayAuthClaimGenerationCurrent({
              kind: 'link-account',
              paymentId,
              generation,
            })
          ) {
            endPostPayAuthClaimInFlight({ kind: 'link-account', paymentId });
          }
          claimGenerationRef.current = null;
          autoLinkAttemptedRef.current = false;
          setAutoLinkSuppressed(true);
        }}
    );
  }, [linkAccount, linked, outcomeLinked, paymentId, session]);

  useEffect(() => {
    if (!signedInChrome || showLinked || autoLinkSuppressed) {
      return;
    }
    // Defer so link mutation setState is not synchronous inside the effect body.
    queueMicrotask(() => {
      handleLink();
    });
  }, [signedInChrome, showLinked, autoLinkSuppressed, handleLink]);

  useEffect(() => {
    if (!linkAccount.isPending) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      const generation = claimGenerationRef.current;
      if (
        generation !== null &&
        isPostPayAuthClaimGenerationCurrent({
          kind: 'link-account',
          paymentId,
          generation,
        })
      ) {
        endPostPayAuthClaimInFlight({ kind: 'link-account', paymentId });
      }
      claimGenerationRef.current = null;
      setLinkTimedOut(true);
      setAutoLinkSuppressed(true);
      autoLinkAttemptedRef.current = false;
      linkAccount.reset();
    }, LINK_ACCOUNT_UI_TIMEOUT_MS);
    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [linkAccount, linkAccount.isPending, paymentId]);

  if (showLinked) {
    return (
      <Card padded>
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">{t('postPay.linkedTitle')}</h2>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('postPay.linkedDescription')}
          </p>
          <Button
            intent="primary"
            block
            size="lg"
            onClick={onContinue}
            disabled={onContinue === undefined}
          >
            {t('postPay.continue')}
          </Button>
        </div>
      </Card>
    );
  }

  if (showSignIn && !signedInChrome) {
    // G1: R1 + global token → soft reclaim; never paint SignInScreen under signed-in shell.
    if (!canMountEmbeddedSignInForm({ session })) {
      return (
        <SignedInAccountPromptCard
          onContinueWithoutAccount={onContinue}
        />
      );
    }
    return (
      <SignInScreen
        heading={t('auth.saveToAccountHeading')}
        helperText={t('auth.saveToAccountHelper')}
        onSignedIn={handleLink}
      />
    );
  }

  if (signedInChrome) {
    return (
      <Card padded>
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">{t('postPay.saveHeading')}</h2>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('postPay.saveDescription')}
          </p>
          {linkAccount.isError ? (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {isTransactionLinkedToOtherCustomerError(linkAccount.error)
                ? t('postPay.linkedConflict')
                : toCustomerFacingErrorMessage(linkAccount.error, {
                    fallback: t('errors.generic'),
                  })}
            </p>
          ) : null}
          {linkTimedOut ? (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {t('errors.linkAccountTimeout')}
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <Button
              intent="primary"
              block
              size="lg"
              onClick={() => {
                handleLink();
              }}
              disabled={linkAccount.isPending}
            >
              {linkAccount.isPending ? t('postPay.linking') : t('postPay.linkAccount')}
            </Button>
            <Button
              intent="ghost"
              block
              size="md"
              onClick={onContinue}
            >
              {t('postPay.continueWithoutLink')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card padded>
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">{t('postPay.promptTitle')}</h2>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('postPay.promptDescription')}
        </p>
        <div className="flex flex-col gap-2">
          <Button
            intent="primary"
            block
            size="lg"
            onClick={() => {
              // G1: durable link claim for OIDC shop-bounce (runner after land).
              // OTP onSignedIn → handleLink still covers in-place verify-otp.
              savePendingPostPayLinkClaim({ paymentId, tenantCode });
              // Park before SignIn so Google/Apple OIDC remounts this surface.
              // Park failure must not block OTP — only hurts OIDC remount.
              parkPostPayAuthReturnTarget(
                `${location.pathname}${location.search}`,
                tenantCode,
              );
              setShowSignIn(true);
            }}
          >
            {t('postPay.saveToAccount')}
          </Button>
          <Button intent="ghost" block size="md" onClick={onContinue}>
            {t('postPay.continueWithoutAccount')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
